import React, { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Picker from '@emoji-mart/react';
import data from '@emoji-mart/data';

interface EmojiPickerProps {
    onEmojiSelect: (emoji: string) => void;
    onClose: () => void;
    triggerRef?: React.RefObject<HTMLElement | null>;
}

export const EmojiPicker: React.FC<EmojiPickerProps> = ({
    onEmojiSelect,
    onClose,
    triggerRef
}) => {
    const pickerRef = useRef<HTMLDivElement>(null);

    // Calcula a posição do picker baseado no trigger
    const getPosition = useCallback(() => {
        if (!triggerRef?.current) {
            return { top: 100, left: 16 };
        }

        const triggerRect = triggerRef.current.getBoundingClientRect();
        const pickerHeight = 400;
        const padding = 8;

        // Posição acima do botão
        let top = triggerRect.top - pickerHeight - padding;
        let left = triggerRect.left;

        // Se não cabe em cima, coloca embaixo
        if (top < padding) {
            top = triggerRect.bottom + padding;
        }

        // Se não cabe à direita, ajusta
        if (left + 350 > window.innerWidth - padding) {
            left = window.innerWidth - 350 - padding;
        }

        // Se não cabe à esquerda
        if (left < padding) {
            left = padding;
        }

        return { top, left };
    }, [triggerRef]);

    const position = getPosition();

    // Fecha o picker ao clicar fora
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (
                pickerRef.current &&
                !pickerRef.current.contains(target) &&
                !triggerRef?.current?.contains(target)
            ) {
                onClose();
            }
        };

        // Fecha com ESC
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [onClose, triggerRef]);

    // Handler para seleção de emoji
    const handleSelect = (emoji: { native: string }) => {
        onEmojiSelect(emoji.native);
    };

    const pickerContent = (
        <div
            ref={pickerRef}
            className="fixed z-[9999]"
            style={{
                top: position.top,
                left: position.left
            }}
        >
            {/* Container do Picker */}
            <div className="bg-white rounded-xl shadow-2xl border border-border overflow-hidden">
                <Picker
                    data={data}
                    onEmojiSelect={handleSelect}
                    locale="pt"
                    theme="light"
                    previewPosition="none"
                    skinTonePosition="none"
                    navPosition="bottom"
                    perLine={8}
                    emojiSize={24}
                    emojiButtonSize={32}
                    maxFrequentRows={1}
                    icons="outline"
                    set="native"
                />
            </div>
        </div>
    );

    // Usa portal para renderizar fora do DOM pai
    return createPortal(pickerContent, document.body);
};
